import { singleton } from 'tsyringe';
import ConfigurationManager from '../../infra/configuration/ConfigurationManager';
import { AccountsClient } from '../../clients/stratus/accountmgtsvc';
import EventNames from '../../config/eventNames';
import { SetServiceDependencies } from '../../infra/startup/types';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import bindAllMethods from '../../utils/bindAllMethods';
import { IAuthProviderService } from '../authProviderService';
import AuthContextEnum from '../authTokenService/AuthContextEnum';
import authContextEnumToTenantLevel from '../authTokenService/utils/authContextEnumToTenantLevel';
import tenantLevelToAuthContextEnum from '../authTokenService/utils/tenantLevelToAuthContextEnum';
import IEventService from '../eventService/IEventService';
import { INavigationService } from '../navigationService';
import { ISessionService } from '../session';
import { ISupportSessionService } from '../supportSession';
import ITenantHandlerService from './ITenantHandlerService';
import {
  CustomBehaviorsType,
  PortalOnboardingType,
  TenantHandlerParamsType
} from './strategy/external-types';
import getTenantStrategy from './strategy/strategy';
import TenantStrategy from './strategy/TenantStrategy';
import {
  CreateTenantResultType,
  StackType,
  TenantDataType,
  TenantHandlerFilterType,
  TenantHandlerStrategyType,
  TenantVisualizationType
} from './strategy/types';
import TenantObserver, { TenantEvents } from './TenantObserver';
import { TenantRepository } from './TenantRepository';
import type { HandledTenant } from './types';
import * as T from './types';
import { executeIfBFCached } from './utils/bfCacheUtils';
import {
  appendCurrentLocation,
  appendTenantRedirect
} from './utils/customBehaviorRedirectUrl';
import { HandledTenantsRepository } from './HandledTenantsRepository';
import connectionInfo from '@clientos/connection-info-northbound-api';
import { TenantsDataRepository } from './TenantDataRepository';
import { IGrantService } from '../grants/IGrantService';
import { LastTenantIdsRepository } from '../../behaviors/beforeInitBehaviors/MultipleTabsDeamon/LastTenantIdsRepository';
import { InternalService } from '../../core/services';
import { ClientOSError } from '../../infra/errors/ClientOSError';
import { ClientOSErrorCodes } from '../../infra/errors/ErrorCodes';
import { AuthPluginFacade } from '../JWeb';
import { IUserService } from '../userService';
import { ILocalizationService } from '../localizationService';

@singleton()
class TenantHandlerService
  extends InternalService
  implements ITenantHandlerService
{
  public HANDLED_TENANTS_SUFFIX = '';
  public TENANT_SUFFIX_CONCAT_STRING = '__';
  public START_TENANT_LEVEL = 1;

  private _initialTenants: T.InitialTenantType[];
  private _tenantRepository: TenantRepository;
  private _tenantsDataRepository: TenantsDataRepository;
  private _stack: StackType;
  private _globalTenantHandler: string;

  private _tenantHandlerParams: TenantHandlerParamsType[];
  private _enable: boolean;
  private _isNative: boolean;

  private _currentTenantHandlerKey: string;
  private _currentTenantHandler: TenantHandlerParamsType;
  private _currentTenantAdditionalData: T.CurrentTenantAdditionalDataType;

  private _handledTenantsRepository: HandledTenantsRepository;
  private _lastTenantIdsRepository: LastTenantIdsRepository;

  private _authProvider: IAuthProviderService;

  private _tenantSelectorAssetReference: string;

  private _tenantStrategyInstanceList: Record<
    string,
    T.TenantStrategyInstanceType[]
  >;

  private _sessionService: ISessionService;
  private _eventService: IEventService;
  private _navigationService: INavigationService;
  private _supportSessionService: ISupportSessionService;
  private _grantService: IGrantService;
  private _userService: IUserService;
  private _accountsClient: AccountsClient;
  private _localizationService: ILocalizationService;

  private _forceTenantSelectionOnPublicRoutes: T.ForceTenantSelectionConfigType;

  private _tenantGroups?: Record<string, TenantHandlerFilterType>;

  private _currentSettingTenant: T.CurrentSettingTenantType = {};

  // TODO: Use app service instead of stack
  constructor() {
    super();
    const tenantHandlerProps =
      ConfigurationManager.getProps('tenantHandlerProps')!;
    const {
      tenantHandlerList,
      stack,
      globalTenantHandler,
      enabled,
      tenantSelectorAssetReference,
      isNative,
      tenantGroups,
      forceTenantSelectionOnPublicRoutes
    } = tenantHandlerProps;

    this._tenantHandlerParams = tenantHandlerList;
    this._stack = stack;
    this._globalTenantHandler = globalTenantHandler;
    this._enable = enabled;
    this._isNative = isNative;
    this._tenantSelectorAssetReference = tenantSelectorAssetReference;
    this._tenantGroups = tenantGroups;
    this._forceTenantSelectionOnPublicRoutes =
      forceTenantSelectionOnPublicRoutes;
    bindAllMethods(this);
  }

  public getForceTenantSelectionOnPublicRoutes(): T.ForceTenantSelectionConfigType {
    return this._forceTenantSelectionOnPublicRoutes;
  }

  public setDependencies({
    repositories,
    services,
    clients
  }: SetServiceDependencies): void {
    const {
      tenantRepository,
      handledTenantsRepository,
      tenantsDataRepository,
      lastTenantIdsRepository
    } = repositories;
    const {
      authProviderService,
      eventService,
      navigationService,
      supportSessionService,
      grantService,
      sessionService,
      userService,
      localizationService
    } = services;
    this._tenantRepository = tenantRepository;
    this._handledTenantsRepository = handledTenantsRepository;
    this._tenantsDataRepository = tenantsDataRepository;
    this._lastTenantIdsRepository = lastTenantIdsRepository;
    this._authProvider = authProviderService;
    this._eventService = eventService;
    this._navigationService = navigationService;
    this._supportSessionService = supportSessionService;
    this._grantService = grantService;
    this._sessionService = sessionService;
    this._accountsClient = clients.stratus.accountsMgtSvcClient;
    this._userService = userService;
    this._localizationService = localizationService;
  }

  public async init(): Promise<void> {
    if (!this.isEnabled()) {
      this._markAsReady();
      return;
    }

    await this._initializeTenantStrategyList();
    this._subscribeToTenantListUpdates();

    this._markAsReady();
  }

  /**
   * Command: Create tenant strategy instance list for all tenant handlers
   */
  private async _initializeTenantStrategyList(): Promise<void> {
    this._tenantStrategyInstanceList = {};
    this._tenantHandlerParams.forEach((t) => {
      this._tenantStrategyInstanceList[t.key] = [];
      this._createTenantInstanceList(t, this.START_TENANT_LEVEL, t.key);
    });
  }

  /**
   * Subscribes to shellTenantListUpdatedEventName so that whenever a strategy
   * fetches a fresh tenant list (e.g. getTenantList({ refresh: true })), any
   * already-handled/selected tenant present in that list gets its cached data
   * (e.g. name) refreshed too - keeping getTenantByContext/getTenantData in sync
   * without every caller having to do it manually.
   */
  private _subscribeToTenantListUpdates(): void {
    void this._eventService?.subscribe(
      EventNames.shellTenantListUpdatedEventName,
      (event) => {
        const tenantList = event?.eventData
          ?.tenantList as TenantVisualizationType[];
        if (!tenantList?.length) {
          internalLogger?.debug?.(
            'TenantHandlerService - _subscribeToTenantListUpdates - Skipping update, received tenantList is empty.'
          );
          return;
        }

        const handledTenants = this.getHandledTenants();
        handledTenants.forEach((handledTenant) => {
          const updatedTenant = tenantList.find(
            (tenant) => tenant.id === handledTenant.id
          );
          if (updatedTenant) {
            this.updateHandledTenantData(
              handledTenant.level,
              handledTenant.id,
              updatedTenant
            );
          }
        });
      }
    );
  }

  public isEnabled(): boolean {
    return this._enable;
  }

  public setTenantHandlerKey = async (
    key: string = this._globalTenantHandler,
    forceReload: boolean = false
  ): Promise<void> => {
    const isLoggedIn = await this._sessionService.isLoggedIn();

    if (!this.isEnabled() || !isLoggedIn) {
      /* istanbul ignore next */
      internalLogger.system(`Skipping setTenantHandlerKey`);
      return;
    }

    if (this._currentTenantHandlerKey !== key || forceReload) {
      const firstTenantLevel = this.START_TENANT_LEVEL;

      this._currentTenantHandlerKey = key || this._globalTenantHandler;

      this._currentTenantHandler = this._getTenantHandlerByKey(
        this._currentTenantHandlerKey
      );

      const hasInternetAccess = await this.hasInternetAccess();
      if (hasInternetAccess && !AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE) {
        /* istanbul ignore next */
        internalLogger.system(
          `@clientos/commons-northbound-api - TenantHandlerService - setTenantHandlerKey - Clearing handled Tenants Repository`
        );
        this._handledTenantsRepository.clear();
      }

      this.createHandleTenantObject(
        this._currentTenantHandler,
        firstTenantLevel
      );

      const handledTenants = this.getHandledTenants();
      await TenantObserver.notify(
        TenantEvents.SET_TENANT_HANDLER_KEY,
        handledTenants
      );
    }
  };

  private async hasInternetAccess(): Promise<boolean> {
    const hasInternetAccess = await connectionInfo.v1.hasInternetAccess();
    return hasInternetAccess.result;
  }

  private getHandledTenants(): HandledTenant[] {
    return (
      this._handledTenantsRepository.findOne(this.HANDLED_TENANTS_SUFFIX) || []
    );
  }

  private _getTenantHandlerByKey = (
    key: string = this._globalTenantHandler
  ): TenantHandlerParamsType => {
    if (!this.isEnabled()) return;

    const tenantHierarchy = this._tenantHandlerParams.find(
      (t) => t.key === key
    );
    if (!tenantHierarchy) internalLogger?.error('TenantKey not found.');

    return tenantHierarchy;
  };

  public getPortalOnboardingList = (): PortalOnboardingType[] => {
    this._currentTenantHandler = this._getTenantHandlerByKey();

    return this._currentTenantHandler?.options?.portalOnboardingList;
  };

  private async resolveCountrySet(
    countrySet: T.CreateTenantOptionsType['countrySet']
  ): Promise<string[] | undefined> {
    if (countrySet?.length) return countrySet;

    const residenceCountry = await this._userService.getResidenceCountry();
    const country =
      residenceCountry || this._localizationService?.country?.toUpperCase();

    return country ? [country] : undefined;
  }

  public async createTenant(
    options: T.CreateTenantOptionsType,
    authContext?: AuthContextEnum
  ): Promise<CreateTenantResultType> {
    if (AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE) {
      console.log(
        '@clientos/commons-norhtbound-api - TenantHandlerService - createTenant - Skipping tenant creation while in fallback state.'
      );
      throw new Error('Cannot create tenant in fallback state');
    }
    try {
      const countrySet = await this.resolveCountrySet(options?.countrySet);

      const language =
        options?.language?.toLowerCase() ||
        this._localizationService?.language?.toLowerCase();

      const idToken = this._sessionService?.getIdToken();

      const level = authContext
        ? authContextEnumToTenantLevel(authContext)
        : this.START_TENANT_LEVEL;

      const optionsToStrategy = {
        idToken,
        language,
        countrySet,
        ...options
      };

      const strategy = this.getTenantStrategy(level);
      return strategy?.createTenant(optionsToStrategy);
    } catch (error) {
      internalLogger?.system({
        class: 'TenantHandlerService',
        method: 'createTenant',
        error
      });
      throw error;
    }
  }

  public getTenantSuffix = (level: number = null): string => {
    const handledTenants = this.getHandledTenants();

    if (handledTenants.length === 0) return '';

    const suffixList = this._getOrderedTenantSuffixes(level);

    return suffixList.join(this.TENANT_SUFFIX_CONCAT_STRING);
  };

  public generateTenantSuffix = (
    authContext: AuthContextEnum,
    tenantId: string
  ): string => {
    const tenantLevel = authContextEnumToTenantLevel(authContext);
    const suffixList = this._getOrderedTenantSuffixes(tenantLevel - 1);
    suffixList.push(tenantId);
    return suffixList.join(this.TENANT_SUFFIX_CONCAT_STRING);
  };

  public getCurrentContext = (): AuthContextEnum => {
    const handledTenants = this.getHandledTenants();

    if (handledTenants.length === 0) return AuthContextEnum.tenantless;

    let highestTenantLevel = 0;
    handledTenants.forEach((t) => {
      if (t.proccessed && t.level > highestTenantLevel)
        highestTenantLevel = t.level;
    });

    return tenantLevelToAuthContextEnum(highestTenantLevel);
  };

  public checkIfTenantIsStoredBySuffix = (sufix: string): boolean => {
    return !!this._tenantRepository.findOne(sufix)?.id;
  };

  public clearTenants = (level = this.START_TENANT_LEVEL): void => {
    if (AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE) return;
    this._validateLevel(level);
    this._tenantRepository.clear(level);
  };

  public getNextUnproccessedTenant = (
    forceAuthContext?: AuthContextEnum
  ): T.HandledTenant & T.TenantStrategyInstanceType => {
    let data;
    const handledTenants = this.getHandledTenants();
    if (forceAuthContext) {
      const level = authContextEnumToTenantLevel(forceAuthContext);
      data = handledTenants.find((t) => t.level === level);
    } else {
      data = handledTenants.find((t) => t.proccessed === false);
    }
    if (!data) return null;
    const strategy = this.getTenantStrategy(data?.level);
    return { ...data, strategy };
  };

  public getTenantId = (level = this.START_TENANT_LEVEL): string => {
    this._validateLevel(level);
    const tenantData = this._tenantRepository.findOne(
      this.getTenantSuffix(level)
    );
    return tenantData?.level === level ? tenantData.id : null;
  };

  public getTenantIdsMap = (): Record<string, string> => {
    const tenantIdsMap = {};
    const handledTenants = this.getHandledTenants();
    handledTenants.forEach((t) => {
      if (t.proccessed) {
        tenantIdsMap[t.strategyEnum] = t.id;
      }
    });
    return tenantIdsMap;
  };

  public getTenantByLevel = (
    level = this.START_TENANT_LEVEL
  ): T.HandledTenant => {
    this._validateLevel(level);
    const handledTenants = this.getHandledTenants();
    return handledTenants.find((t) => t.level === level);
  };

  public getTenantByContext = (
    authContext: AuthContextEnum
  ): T.HandledTenant => {
    const tenantLevel = authContextEnumToTenantLevel(authContext);
    const handledTenants = this.getHandledTenants();
    return handledTenants.find((t) => t.level === tenantLevel && t.proccessed);
  };

  public getTenantStrategy(level = this.START_TENANT_LEVEL): TenantStrategy {
    return this._tenantStrategyInstanceList?.[
      `${this._currentTenantHandlerKey}`
    ]?.find((t) => t.level === level)?.strategy;
  }

  /**
   * Updates the cached data (e.g. name) of an already handled/selected tenant
   * without re-running the tenant exchange flow. Used to refresh the data
   * returned by getTenantByContext/getTenantData after a fresh tenant list
   * fetch (e.g. getTenantList({ refresh: true })).
   */
  public updateHandledTenantData = (
    level: number,
    tenantId: string,
    data: TenantDataType
  ): void => {
    this._validateLevel(level);
    const handledTenants = this.getHandledTenants();
    const handledTenant = handledTenants.find(
      (t) => t.level === level && t.id === tenantId
    );

    if (!handledTenant) {
      internalLogger?.debug?.(
        `TenantHandlerService - updateHandledTenantData - Skipping update, no handled tenant found for level: ${level}, tenantId: ${tenantId}.`
      );
      return;
    }

    handledTenant.data = {
      name: data?.name,
      type: data?.type,
      roleCategory: data?.roleCategory,
      roleName: data?.roleName,
      roles: data?.roles || []
    };

    this._handledTenantsRepository.save(
      this.HANDLED_TENANTS_SUFFIX,
      handledTenants
    );
  };

  /**
   * Here we set the tenant, and if the tenant is not proccessed, we will check if the tenant has a custom behavior and redirect to the custom behavior URL.
   */
  public setTenant = async (
    tenantId: string,
    level: number,
    options: { reload?: boolean; useCustomBehavior?: boolean },
    data?: TenantDataType
  ): Promise<boolean> => {
    console.log(
      `@clientos/commons-northbound-api - TenantHandlerService - setTenant - Starting setting tenant for: ${tenantId}.`
    );

    const currentSettingTenantId = this._currentSettingTenant?.tenantId;
    const settingPromise = this._currentSettingTenant?.promise;
    const isSameTenantId = currentSettingTenantId === tenantId;

    // If the same tenantId is already being set, return the existing promise
    if (currentSettingTenantId && isSameTenantId) {
      internalLogger?.debug?.(
        `setTenant already in progress for tenantId: ${tenantId}, returning existing promise`
      );
      return settingPromise;
    }

    // await the promise of a different tenant finish to continue
    if (currentSettingTenantId && !isSameTenantId) {
      console.debug(
        `@clientos/commons-northbound-api - TenantHandlerService - setTenant - setTenant in progress for tenantId: ${currentSettingTenantId}, awaiting promise`
      );
      await settingPromise;
    }

    const promise = this._executeSetTenant(
      tenantId,
      level,
      options,
      data
    ).finally(() => {
      console.log(
        `@clientos/commons-northbound-api - TenantHandlerService - setTenant - Finishing setting tenant for: ${tenantId}`
      );
      this._currentSettingTenant = {};
    });

    this._currentSettingTenant = { tenantId, promise };
    return promise;
  };

  private async _executeSetTenant(
    tenantId: string,
    level: number,
    options: { reload?: boolean; useCustomBehavior?: boolean },
    data?: TenantDataType
  ): Promise<boolean> {
    if (!this._currentTenantHandlerKey) {
      // In case setTenant is not ready to run yet, we need to wait for the TenantHandlerKey to be set and invoke this method again.
      /* istanbul ignore next */
      internalLogger?.debug?.d4?.({
        className: 'TenantHandlerService',
        methodName: 'setTenant()',
        message: `TenantHandlerKey is not set, subscribing to TenantObserver to set tenant ${tenantId} at level ${level}.`,
        variables: {
          tenantId,
          level,
          options,
          data
        }
      });

      const cb = async () => {
        await this.setTenant(tenantId, level, options, data);
        TenantObserver.unsubscribe(TenantEvents.SET_TENANT_HANDLER_KEY, cb);
      };
      TenantObserver.subscribe(TenantEvents.SET_TENANT_HANDLER_KEY, cb);
      return;
    }

    this._validateLevel(level);

    await TenantObserver.notify(TenantEvents.SET_TENANT_STARTED, []);

    const shouldUseCustomBehaviors =
      options?.useCustomBehavior &&
      !this._supportSessionService.isSupportSession();

    const pendingCustomBehaviors = shouldUseCustomBehaviors
      ? await this.checkCustomBehaviorAndRedirect(level, tenantId)
      : null;

    const handledTenants = this.getHandledTenants();
    const handledTenant =
      handledTenants.find((t) => t.level === level) || ({} as HandledTenant);
    const oldHandledTenant: T.HandledTenant = { ...handledTenant };

    try {
      handledTenant.id = tenantId;
      handledTenant.proccessed = true;
      handledTenant.data = data;

      this._handledTenantsRepository.save(
        this.HANDLED_TENANTS_SUFFIX,
        handledTenants
      );

      const tenantStrategy = this.getTenantStrategy(level);
      await tenantStrategy.setTenant(
        tenantId,
        tenantLevelToAuthContextEnum(level)
      );

      await this.setTenantId(handledTenant, tenantId, level, data);
    } catch (e) {
      handledTenant.id = oldHandledTenant?.id;
      handledTenant.proccessed = oldHandledTenant?.proccessed;
      handledTenant.data = oldHandledTenant?.data;

      this._handledTenantsRepository.save(
        this.HANDLED_TENANTS_SUFFIX,
        handledTenants
      );

      internalLogger?.error(
        `[${ClientOSErrorCodes.TENANT_SET_FAILED}] Tenant ${tenantId} wasn't able to be exchanged.`,
        e
      );

      return Promise.resolve(false);
    }

    await TenantObserver.notify(
      TenantEvents.SET_TENANT,
      this.getHandledTenants()
    );

    await this._handlePendingCustomBehaviors(
      pendingCustomBehaviors,
      level,
      tenantId
    );

    this._publishTenantEvent(EventNames.shellTenantChangedEventName, {
      preventReload: !options?.reload
    } as T.TenantChangeEvent);

    return Promise.resolve(true);
  }

  private _publishTenantEvent(
    eventName: EventNames,
    payload: Record<string, unknown>
  ): void {
    this._eventService.publish(eventName, payload);
  }

  public onTenantChange(
    cb: (options: T.TenantChangeEvent) => void
  ): () => void {
    const removeListenerPromise = this._eventService.subscribe(
      EventNames.shellTenantChangedEventName,
      (event) => cb(event.eventData)
    );

    return () => removeListenerPromise.then((result) => result.unsubscribe());
  }

  public getHandledTenantList = (): HandledTenant[] => {
    return this.getHandledTenants();
  };

  public setInitialTenants(initialTenants: T.InitialTenantType[]): void {
    this._initialTenants = initialTenants;
  }

  public getInitialTenants(): T.InitialTenantType[] {
    return this._initialTenants;
  }

  public getTenantListFromCache({
    authContext
  }: {
    authContext: AuthContextEnum;
  }): TenantVisualizationType[] {
    const resolvedContext = authContext || this.getCurrentContext();
    const tenantLevel = authContextEnumToTenantLevel(resolvedContext);

    if (tenantLevel < this.START_TENANT_LEVEL) {
      internalLogger?.warn?.(
        `getTenantListFromCache: tenant level ${tenantLevel} (authContext: ${resolvedContext}) is below the start level ${this.START_TENANT_LEVEL}. Returning an empty list.`
      );
      return [];
    }

    const tenantStrategy = this.getTenantStrategy(tenantLevel);

    if (!tenantStrategy) {
      internalLogger?.warn?.(
        `getTenantListFromCache: tenant strategy not found for level ${tenantLevel}`
      );
      return [];
    }

    try {
      return tenantStrategy.getTenantListFromCache();
    } catch (error) {
      internalLogger?.error?.(
        'getTenantListFromCache: failed to read tenant cache',
        error
      );
      return [];
    }
  }

  public async getTenantList({
    authContext,
    refreshList
  }: {
    authContext: AuthContextEnum;
    refreshList?: boolean;
  }): Promise<TenantVisualizationType[]> {
    const tenantLevel = authContextEnumToTenantLevel(authContext);
    return this.getTenantStrategy(tenantLevel).getTenantList(refreshList);
  }

  public async getCurrentTenantAdditionalData(): Promise<T.CurrentTenantAdditionalDataType> {
    if (this.getCurrentContext() === AuthContextEnum.tenantless) return null;
    if (!this._currentTenantAdditionalData) {
      const { countrySet, regionId } = await this._accountsClient.getAccount();
      this._currentTenantAdditionalData = {
        countries: countrySet,
        regionId
      };
    }
    return this._currentTenantAdditionalData;
  }

  private setTenantId = async (
    handledTenant: T.HandledTenant,
    tenantId: string,
    level = this.START_TENANT_LEVEL,
    data: TenantDataType
  ) => {
    this._validateLevel(level);
    const tenantStrategy = this.getTenantStrategy(level);

    const tenantStorage = {
      id: tenantId,
      strategy: tenantStrategy.getStrategy(),
      tenantHandlerKey: this._currentTenantHandlerKey,
      level,
      data
    };

    if (!data) {
      const tenantData = await tenantStrategy.getTenantById(tenantId);

      tenantStorage.data = {
        type: tenantData.type,
        name: tenantData.name,
        roleCategory: tenantData.roleCategory,
        roleName: tenantData?.roleName,
        roles: tenantData?.roles || []
      };

      handledTenant.data = tenantStorage.data;
    }

    const handledTenants = this.getHandledTenants();
    const existingTenant = handledTenants.find(
      (tenant) => tenant.level === handledTenant.level
    );
    if (!existingTenant) {
      handledTenants.push(handledTenant);
    } else {
      existingTenant.id = handledTenant.id;
      existingTenant.data = handledTenant.data;
      existingTenant.proccessed = handledTenant.proccessed;
    }

    this._handledTenantsRepository.save(
      this.HANDLED_TENANTS_SUFFIX,
      handledTenants
    );

    const tenantSuffix = this.getTenantSuffix(level);
    this._tenantRepository.save(tenantSuffix, tenantStorage);

    this.unProccessChainedHandledTenants(level + 1);
    return tenantStorage;
  };

  private _getInitialTenantSuffix(level) {
    this._validateLevel(level);
    const tenantIds = [];
    this._initialTenants?.forEach((t) => {
      if (t.level > level) return;
      tenantIds.push(t.id);
    });

    return tenantIds.join(this.TENANT_SUFFIX_CONCAT_STRING);
  }

  private createHandleTenantObject = (
    tenantHierarchy: TenantHandlerParamsType,
    level = this.START_TENANT_LEVEL,
    resetProccess = false
  ): T.HandledTenant => {
    this._validateLevel(level);
    const strategy = tenantHierarchy?.strategy;

    const sufix = this._getInitialTenantSuffix(level);

    const storedTenant = this._tenantRepository.findOne(
      sufix || this.getTenantSuffix(level)
    );

    const isProccessed = this._checkIfTenantIsProccessed(
      tenantHierarchy,
      storedTenant,
      strategy
    );

    const handledTenant: T.HandledTenant = {
      level,
      id: storedTenant?.id,
      proccessed: isProccessed,
      data: storedTenant?.data,
      strategyEnum: strategy
    };

    const handledTenants = this.getHandledTenants();
    const existingTenant = handledTenants.find(
      (tenant) => tenant.level === handledTenant.level
    );
    if (!existingTenant) {
      handledTenants.push(handledTenant);
    } else {
      existingTenant.id = handledTenant.id;
      existingTenant.data = handledTenant.data;
      existingTenant.proccessed = handledTenant.proccessed;
    }
    this._handledTenantsRepository.save(
      this.HANDLED_TENANTS_SUFFIX,
      handledTenants
    );

    if (tenantHierarchy.subTenant) {
      this.createHandleTenantObject(
        tenantHierarchy.subTenant,
        level + 1,
        resetProccess || isProccessed
      );
    }

    return handledTenant;
  };

  private _validateLevel = (level: number) => {
    if (level < this.START_TENANT_LEVEL)
      /* istanbul ignore next */
      new ClientOSError( // NOSONAR
        `The tenant level must be ${this.START_TENANT_LEVEL} or higher.`,
        ClientOSErrorCodes.TENANT_LEVEL_VALIDATION_FAILED,
        null
      );
  };

  private unProccessChainedHandledTenants = (
    level = this.START_TENANT_LEVEL
  ) => {
    this._validateLevel(level);
    const handledTenants = this.getHandledTenants();
    handledTenants.forEach((t) => {
      if (t.level >= level) {
        t.proccessed = false;
        t.data = null;
      }
    });

    this._handledTenantsRepository.save(
      this.HANDLED_TENANTS_SUFFIX,
      handledTenants
    );
  };

  private _createTenantInstanceList(
    tenantHierarchy: TenantHandlerParamsType,
    level: number,
    key: string
  ) {
    const attributes: TenantHandlerStrategyType = {
      stack: this._stack,
      authProvider: this._authProvider,
      assetReference:
        tenantHierarchy?.assetReference || this._tenantSelectorAssetReference,
      options: tenantHierarchy?.options,
      sessionService: this._sessionService,
      eventService: this._eventService,
      isNative: this._isNative,
      isAST: this._supportSessionService.isSupportSession(),
      tenantsDataRepository: this._tenantsDataRepository
    };
    const tenantStrategy = getTenantStrategy(
      tenantHierarchy.strategy,
      attributes
    );

    this._tenantStrategyInstanceList[`${key}`].push({
      level,
      strategy: tenantStrategy
    });

    !!tenantHierarchy.subTenant &&
      this._createTenantInstanceList(tenantHierarchy.subTenant, level + 1, key);
  }

  private _checkIfTenantIsProccessed = (
    tenantHierarchy: TenantHandlerParamsType,
    tenant: T.TenantStorageType,
    strategy: string
  ): boolean => {
    const tenantFilter = tenantHierarchy?.options?.filter;
    const isValid = this.getTenantStrategy(tenant?.level)?.isTenantValid(
      tenantFilter,
      {
        id: tenant?.id,
        name: tenant?.data?.name,
        type: tenant?.data?.type,
        roleCategory: tenant?.data?.roleCategory,
        roleName: tenant?.data?.roleName
      }
    );
    return tenant?.strategy === strategy && isValid;
  };

  /**
   * Checks if the selected tenant matches any custom behavior. If a match is found,
   * it will redirect immediately when there are NO pending grant behaviors to check.
   * Otherwise, it will return the matched behavior and any pending grant behaviors
   * to be checked after the tenant is set.
   */
  public async checkCustomBehaviorAndRedirect(
    level: number,
    tenantId: string
  ): Promise<{
    matchedBehavior: CustomBehaviorsType;
    grantBehaviors: CustomBehaviorsType[];
  }> {
    const customBehaviorsList = this._getTenantHandlerByKey(
      this._currentTenantHandlerKey
    )?.options?.customBehaviors;
    if (!customBehaviorsList || customBehaviorsList.length === 0) {
      return null;
    }

    const authContext = tenantLevelToAuthContextEnum(level);

    const tenantList = await this.getTenantList({ authContext });
    const selectedTenant = tenantList.find((tenant) => tenant?.id === tenantId);

    const { matchedBehavior, grantBehaviors } =
      this.findCustomBehavior(customBehaviorsList, selectedTenant, level) || {};

    // If we have a matched behavior, and there are no pending grant behaviors to check, we can redirect immediately.
    if (matchedBehavior && grantBehaviors?.length === 0) {
      internalLogger?.debug.d4({
        message: `Tenant ${tenantId} matched custom behavior, redirecting`,
        variables: { behavior: matchedBehavior },
        className: 'TenantHandlerService',
        methodName: 'checkCustomBehaviorAndRedirect'
      });
      await this._redirectByCustomBehavior(matchedBehavior, tenantId);
    }

    return { matchedBehavior, grantBehaviors };
  }

  /**
   * If there are pending grant behaviors to check after setting the tenant,
   * we check them here and redirect if needed. When a grant behavior doesn't
   * specify a redirectTo, it will remain in the original portal.
   */
  private async _handlePendingCustomBehaviors(
    pendingCustomBehaviors: {
      matchedBehavior: CustomBehaviorsType;
      grantBehaviors: CustomBehaviorsType[];
    },
    level: number,
    tenantId: string
  ): Promise<void> {
    if (!pendingCustomBehaviors?.grantBehaviors?.length) {
      return;
    }

    const matchingGrantBehavior = await this._getFirstMatchingGrantBehavior(
      pendingCustomBehaviors.grantBehaviors
    );

    // Determine which behavior to use for redirect
    const behaviorToRedirect =
      matchingGrantBehavior || pendingCustomBehaviors?.matchedBehavior;

    internalLogger?.debug.d4({
      message: `Tenant ${tenantId} matched grant custom behavior`,
      variables: { behavior: behaviorToRedirect },
      className: 'TenantHandlerService',
      methodName: '_handlePendingCustomBehaviors'
    });

    if (behaviorToRedirect?.onSelect?.redirectTo) {
      this._clearHandledTenants(level);
      this._lastTenantIdsRepository.clear();
      await this._redirectByCustomBehavior(behaviorToRedirect, tenantId);
    }
  }

  private async _redirectByCustomBehavior(
    behavior: CustomBehaviorsType,
    tenantId: string
  ): Promise<void> {
    if (!behavior?.onSelect) {
      return;
    }

    // If the user comes back to the previous tenant's cached page, refresh.
    executeIfBFCached(this._navigationService.refreshPath);

    this._navigationService.redirect(
      this._buildCustomBehaviorRedirectUrl(behavior.onSelect, tenantId)
    );

    // Block execution
    await new Promise(() => true);
  }

  /**
   * Builds the redirect URL for a matched custom behavior. `includeTenantId`
   * appends the legacy `/login` path segment and `t1=<tenantId>` query param.
   * When combined with `includeTenantId`, `includePath`/`includeQueryparams`
   * forward the current path/query params via the `postLoginRedirect` query
   * param instead, so the target portal restores them after login. Without
   * `includeTenantId`, they are appended directly onto `redirectTo`.
   */
  private _buildCustomBehaviorRedirectUrl(
    onSelect: CustomBehaviorsType['onSelect'],
    tenantId: string
  ): string {
    const { redirectTo, includeTenantId, includePath, includeQueryparams } =
      onSelect;

    if (!includeTenantId && !includePath && !includeQueryparams) {
      return redirectTo;
    }

    const { pathname: currentPathname, search: currentSearch = '' } =
      this._navigationService.location;

    const isAbsolute =
      redirectTo.startsWith('http://') || redirectTo.startsWith('https://');
    // Use a placeholder origin so relative paths can be parsed uniformly;
    // only pathname/search are used from the resulting URL.
    const targetUrl = isAbsolute
      ? new URL(redirectTo)
      : new URL(redirectTo, 'https://_placeholder_');

    if (includeTenantId) {
      appendTenantRedirect({
        targetUrl,
        tenantId,
        includePath,
        includeQueryparams,
        currentPathname,
        currentSearch
      });
    } else {
      appendCurrentLocation({
        targetUrl,
        includePath,
        includeQueryparams,
        currentPathname,
        currentSearch
      });
    }

    return isAbsolute
      ? targetUrl.toString()
      : targetUrl.pathname + targetUrl.search;
  }

  private _getOrderedTenantSuffixes = (level: number = null): string[] => {
    const currentTenants: string[] = [];
    const handledTenants = this.getHandledTenants();
    handledTenants.forEach((t) => {
      if (level && t.level > level) {
        return;
      }
      t.proccessed && currentTenants.push(t.id);
    });
    return currentTenants;
  };

  /**
   * Returns the FIRST custom behavior of the array that matches the tenant.
   * If an item with a filter of type "grant" is found during the iteration
   * it will be returned as a grantBehavior, to be checked *after* the tenant
   * is set.
   */
  public findCustomBehavior(
    customBehaviors: CustomBehaviorsType[] = [],
    tenant: TenantVisualizationType,
    level: number
  ): {
    matchedBehavior: CustomBehaviorsType;
    grantBehaviors: CustomBehaviorsType[];
  } {
    if (!customBehaviors || customBehaviors.length === 0) return null;
    const pendingCriterionBehaviors: CustomBehaviorsType[] = [];

    if (customBehaviors && tenant && level) {
      for (const customBehavior of customBehaviors) {
        if (customBehavior?.filter?.grant) {
          pendingCriterionBehaviors.push(customBehavior);
          continue;
        }

        if (
          this.getTenantStrategy(level).isTenantValid(
            customBehavior?.filter,
            tenant
          )
        ) {
          return {
            matchedBehavior: customBehavior,
            grantBehaviors: pendingCriterionBehaviors
          };
        }
      }
    }

    return pendingCriterionBehaviors.length
      ? {
          matchedBehavior: null,
          grantBehaviors: pendingCriterionBehaviors
        }
      : null;
  }

  private async _getFirstMatchingGrantBehavior(
    grantBehaviors: CustomBehaviorsType[]
  ) {
    for (const grantBehavior of grantBehaviors) {
      const matches = await this._grantService.checkGrants([
        {
          grant: grantBehavior?.filter?.grant
        }
      ]);
      if (matches) {
        return grantBehavior;
      }
    }
    return null;
  }

  private _clearHandledTenants(level: number): void {
    const handledTenants = this.getHandledTenants();
    const handledTenant = handledTenants.find((t) => t.level === level);
    handledTenant.id = null;
    handledTenant.proccessed = false;
    handledTenant.data = null;
    this._handledTenantsRepository.save(
      this.HANDLED_TENANTS_SUFFIX,
      handledTenants
    );
  }

  public getTenantGroups(): string[] {
    const tenantData = this.getTenantByLevel(this.START_TENANT_LEVEL);
    if (!this._tenantGroups || !tenantData) {
      return [];
    }
    const resultTenantGroups: string[] = [];
    Object.keys(this._tenantGroups).forEach((key) => {
      const tenantFilter = this._tenantGroups[key];
      const isValid = this.getTenantStrategy(tenantData?.level)?.isTenantValid(
        tenantFilter,
        {
          id: tenantData?.id,
          name: tenantData?.data?.name,
          type: tenantData?.data?.type,
          roleCategory: tenantData?.data?.roleCategory,
          roleName: tenantData?.data?.roleName
        }
      );
      if (isValid) {
        resultTenantGroups.push(key);
      }
    });
    return resultTenantGroups;
  }

  public useReactGetTenantList(
    _React: any,
    options: { authContext: AuthContextEnum }
  ): T.UseReactGetTenantListResponseType {
    const [result, setResult] = _React.useState({
      data: this.getTenantListFromCache({ authContext: options?.authContext }),
      error: undefined,
      loading: true
    });
    const isMountedRef = _React.useRef(true);
    const dependenciesRef = _React.useRef({
      options,
      handleDataCount: 0
    });
    dependenciesRef.current.options = options;

    const handleData = _React.useCallback(
      async ({ authContext, refreshList }) => {
        try {
          await this.getTenantList({
            authContext: authContext,
            refreshList: refreshList
          });
        } catch (error) {
          if (!isMountedRef.current) {
            /* istanbul ignore next */
            internalLogger.warn(
              'useReactGetTenantList: component unmounted before receiving error'
            );
            return;
          }

          // E.3: UI hook — log the error but do not re-throw (React handles via error state).
          internalLogger.error(
            'useReactGetTenantList: failed to fetch tenant list',
            error
          );

          setResult((prev) => ({
            data: prev?.data,
            error,
            loading: false
          }));
        }
      },
      []
    );

    _React.useEffect(() => {
      isMountedRef.current = true;
      handleData({
        authContext: dependenciesRef.current.options?.authContext,
        refreshList: true
      });
      return () => {
        isMountedRef.current = false;
      };
    }, [handleData, options?.authContext]);

    _React.useEffect(() => {
      const unsubscribePromise = this._eventService.subscribe(
        EventNames.shellTenantListUpdatedEventName,
        (data) => {
          if (!isMountedRef.current) {
            /* istanbul ignore next */
            internalLogger.warn(
              'useReactGetTenantList: component unmounted before receiving event data'
            );
            return;
          }

          setResult({
            data: data?.eventData?.tenantList,
            error: undefined,
            loading: false
          });
        }
      );

      return () => {
        void unsubscribePromise.then((result) => result.unsubscribe());
      };
    }, [handleData]);

    return _React.useMemo(
      () => ({
        ...result,
        reload: () =>
          handleData({
            authContext: dependenciesRef.current.options?.authContext,
            refreshList: true
          })
      }),
      [result, handleData]
    );
  }
}

export default TenantHandlerService;
